import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Card } from "@mui/material";
import { DmsAnalyticsContext } from "./DmsAnalyticsContext";
import { CHANNEL_OPTIONS, CURRENT_YEAR, ALL_YEARS_VALUE } from "./constants";

// View mode constants for traceability filter
export const VIEW_MODE_OPTIONS = {
    ALL: "all",      // Sem Filtro - shows all data
    STOCK: "stock",  // Saldo - focus on stock/inventory
    SALES: "sales",  // Venda - focus on sales
};

export const DmsAnalyticsCard = ({
    children,
    defaultYears,
    defaultStartMonth = 1,
    defaultEndMonth = 12,
    defaultChannel = CHANNEL_OPTIONS.ALL,
    defaultViewMode = VIEW_MODE_OPTIONS.ALL,
    onRefresh,
    sx,
    ...props
}) => {
    const [filters, setFilters] = useState({
        years: defaultYears || [CURRENT_YEAR],
        startMonth: defaultStartMonth,
        endMonth: defaultEndMonth,
        channel: defaultChannel,
    });

    // View mode state for traceability (Saldo/Venda/Sem Filtro)
    const [viewMode, setViewMode] = useState(defaultViewMode);

    // Pending cross-filters: selected but not yet applied
    const [pendingCrossFilters, setPendingCrossFilters] = useState({
        product: [],
        client: [],
        batch: [],
        uf: [],
    });

    // Applied cross-filters: used for API queries
    const [appliedCrossFilters, setAppliedCrossFilters] = useState({
        product: [],
        client: [],
        batch: [],
        uf: [],
    });

    const [filtersAnchorEl, setFiltersAnchorEl] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const updateFilter = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    const clearFilter = useCallback((key) => {
        if (key === "period") {
            setFilters((prev) => ({ ...prev, startMonth: 1, endMonth: 12 }));
        } else if (key === "years") {
            setFilters((prev) => ({ ...prev, years: [CURRENT_YEAR] }));
        } else if (key === "channel") {
            setFilters((prev) => ({ ...prev, channel: CHANNEL_OPTIONS.ALL }));
        }
    }, []);

    const clearAllFilters = useCallback(() => {
        setFilters({
            years: defaultYears || [CURRENT_YEAR],
            startMonth: 1,
            endMonth: 12,
            channel: CHANNEL_OPTIONS.ALL,
        });
        setPendingCrossFilters({ product: [], client: [], batch: [], uf: [] });
        setAppliedCrossFilters({ product: [], client: [], batch: [], uf: [] });
        setViewMode(defaultViewMode);
    }, [defaultYears, defaultViewMode]);

    // Toggle pending cross-filter: add if not exists, remove if exists
    const toggleCrossFilter = useCallback((type, value, label) => {
        setPendingCrossFilters((prev) => {
            const current = prev[type] || [];
            const exists = current.some((item) => item.value === value);

            if (exists) {
                return {
                    ...prev,
                    [type]: current.filter((item) => item.value !== value),
                };
            } else {
                return {
                    ...prev,
                    [type]: [...current, { value, label }],
                };
            }
        });
    }, []);

    // Apply pending filters - this triggers the API calls
    const applyFilters = useCallback(() => {
        setAppliedCrossFilters({ ...pendingCrossFilters });
        setRefreshKey((prev) => prev + 1);
    }, [pendingCrossFilters]);

    // Clear a specific cross-filter type (from both pending and applied)
    const clearCrossFilterType = useCallback((type) => {
        setPendingCrossFilters((prev) => ({ ...prev, [type]: [] }));
        setAppliedCrossFilters((prev) => ({ ...prev, [type]: [] }));
    }, []);

    // Clear all cross-filters
    const clearAllCrossFilters = useCallback(() => {
        setPendingCrossFilters({ product: [], client: [], batch: [], uf: [] });
        setAppliedCrossFilters({ product: [], client: [], batch: [], uf: [] });
    }, []);

    const refresh = useCallback(() => {
        setRefreshKey((prev) => prev + 1);
        if (onRefresh) {
            onRefresh();
        }
    }, [onRefresh]);

    const openFilters = useCallback((event) => {
        setFiltersAnchorEl(event.currentTarget);
    }, []);

    const closeFilters = useCallback(() => {
        setFiltersAnchorEl(null);
    }, []);

    // Query params uses APPLIED filters (not pending)
    const queryParams = useMemo(() => {
        const params = new URLSearchParams();

        if (filters.channel !== CHANNEL_OPTIONS.ALL) {
            params.append("channel", filters.channel);
        }
        // Handle multiple years - only append if not "all"
        const isAllYears = filters.years.length === 1 && filters.years[0] === ALL_YEARS_VALUE;
        if (filters.years && filters.years.length > 0 && !isAllYears) {
            params.append("years", filters.years.join(","));
        }
        if (filters.startMonth) {
            params.append("start_month", filters.startMonth);
        }
        if (filters.endMonth) {
            params.append("end_month", filters.endMonth);
        }

        // Include view_mode for traceability filter
        if (viewMode && viewMode !== VIEW_MODE_OPTIONS.ALL) {
            params.append("view_mode", viewMode);
        }

        // Use APPLIED cross-filters for API calls
        Object.entries(appliedCrossFilters).forEach(([type, items]) => {
            if (items && items.length > 0) {
                const values = items.map((item) => item.value).join(",");
                params.append(`cross_filter_${type}`, values);
            }
        });

        return params.toString();
    }, [filters, appliedCrossFilters, viewMode]);

    // Check if pending filters differ from applied filters
    const hasPendingChanges = useMemo(() => {
        const pendingStr = JSON.stringify(pendingCrossFilters);
        const appliedStr = JSON.stringify(appliedCrossFilters);
        return pendingStr !== appliedStr;
    }, [pendingCrossFilters, appliedCrossFilters]);

    // Check if any cross-filter is selected (pending)
    const hasCrossFilters = useMemo(() => {
        return Object.values(pendingCrossFilters).some((arr) => arr && arr.length > 0);
    }, [pendingCrossFilters]);

    // Check if any cross-filter is applied
    const hasAppliedCrossFilters = useMemo(() => {
        return Object.values(appliedCrossFilters).some((arr) => arr && arr.length > 0);
    }, [appliedCrossFilters]);

    const isFiltered = useMemo(() => {
        const isDefaultYears = filters.years.length === 1 && filters.years[0] === CURRENT_YEAR;
        return (
            filters.channel !== CHANNEL_OPTIONS.ALL ||
            !isDefaultYears ||
            filters.startMonth !== 1 ||
            filters.endMonth !== 12 ||
            hasAppliedCrossFilters ||
            viewMode !== VIEW_MODE_OPTIONS.ALL
        );
    }, [filters, hasAppliedCrossFilters, viewMode]);

    const contextValue = useMemo(
        () => ({
            filters,
            crossFilters: pendingCrossFilters,  // For UI display
            appliedCrossFilters,                 // For checking applied state
            queryParams,
            refreshKey,
            isFiltered,
            hasCrossFilters,
            hasAppliedCrossFilters,
            hasPendingChanges,
            filtersAnchorEl,
            viewMode,                            // View mode for traceability (saldo/venda/all)
            updateFilter,
            clearFilter,
            clearAllFilters,
            toggleCrossFilter,
            applyFilters,
            clearCrossFilterType,
            clearAllCrossFilters,
            refresh,
            openFilters,
            closeFilters,
            setViewMode,                         // Setter for view mode
        }),
        [
            filters,
            pendingCrossFilters,
            appliedCrossFilters,
            queryParams,
            refreshKey,
            isFiltered,
            hasCrossFilters,
            hasAppliedCrossFilters,
            hasPendingChanges,
            filtersAnchorEl,
            viewMode,
            updateFilter,
            clearFilter,
            clearAllFilters,
            toggleCrossFilter,
            applyFilters,
            clearCrossFilterType,
            clearAllCrossFilters,
            refresh,
            openFilters,
            closeFilters,
            setViewMode,
        ]
    );

    return (
        <DmsAnalyticsContext.Provider value={contextValue}>
            <Card
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    ...sx,
                }}
                {...props}
            >
                {children}
            </Card>
        </DmsAnalyticsContext.Provider>
    );
};

DmsAnalyticsCard.propTypes = {
    children: PropTypes.node,
    defaultYears: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    defaultStartMonth: PropTypes.number,
    defaultEndMonth: PropTypes.number,
    defaultChannel: PropTypes.string,
    defaultViewMode: PropTypes.oneOf(Object.values(VIEW_MODE_OPTIONS)),
    onRefresh: PropTypes.func,
    sx: PropTypes.object,
};
